import React, { useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatGBP } from "../../lib/format";
import { X } from "@phosphor-icons/react";

/**
 * Fully self-contained refund modal. Parent passes a loaded payload ({order, items, payment})
 * as `order` prop. On success it calls `onDone()` so the parent can reload data.
 */
export default function RefundModal({ order, onClose, onDone }) {
  const [amount, setAmount] = useState(String(order?.order?.total?.toFixed(2) || ""));
  const [reason, setReason] = useState("requested_by_customer");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!order) return null;

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.order.order_id}/refund`, {
        amount: amt,
        reason,
        note,
      });
      toast.success(`Refund ${data.stripe_status || "submitted"} · £${amt.toFixed(2)}`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refund failed");
    }
    setBusy(false);
  };

  return (
    <div
      data-testid="refund-modal"
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-heading text-xl font-bold">Issue refund</h3>
            <p className="text-xs text-[#5C5C54]">
              {order.order.vendor_name} · Order #{order.order.order_id.slice(-8)}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F2EFE9] rounded-full" aria-label="Close">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-[#F9F7F3] rounded-xl text-xs space-y-1">
          <p>Order total: <strong>{formatGBP(order.order.total)}</strong></p>
          {order.payment && (
            <>
              <p>Payment: {formatGBP(order.payment.amount_total)} · <span className="capitalize">{order.payment.status}</span></p>
              {order.payment.refunded_amount > 0 && (
                <p className="text-[#A94438]">Already refunded: {formatGBP(order.payment.refunded_amount)}</p>
              )}
            </>
          )}
        </div>

        <label className="block text-xs font-bold uppercase tracking-wider text-[#5C5C54] mb-1">Amount (GBP)</label>
        <input
          data-testid="refund-amount-input"
          type="number"
          step="0.01"
          min="0.01"
          max={order.order.total}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="input-field w-full mb-3"
        />

        <label className="block text-xs font-bold uppercase tracking-wider text-[#5C5C54] mb-1">Reason</label>
        <select
          data-testid="refund-reason-select"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="input-field w-full mb-3"
        >
          <option value="requested_by_customer">Requested by customer</option>
          <option value="duplicate">Duplicate charge</option>
          <option value="fraudulent">Fraudulent</option>
        </select>

        <label className="block text-xs font-bold uppercase tracking-wider text-[#5C5C54] mb-1">Internal note (optional)</label>
        <textarea
          data-testid="refund-note-input"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="input-field w-full mb-4"
          placeholder="e.g. item arrived damaged — photo in email"
        />

        <div className="flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button
            data-testid="refund-submit-btn"
            onClick={submit}
            disabled={busy}
            className="btn-primary flex-1 disabled:opacity-50"
          >
            {busy ? "Processing…" : `Refund ${amount ? formatGBP(parseFloat(amount)) : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}
